import * as React from 'react';
import { useEffect, useState } from 'react';
import { Dimensions, View, StyleSheet } from 'react-native';
import { DatePickerInputFormProps } from './types';
import { AvailableValidationRules } from './validation';
import { useTheme, Text } from 'react-native-paper';
import { Border, Color } from '@/GlobalStyles';
import { DatePickerInput } from '@/components/CustomRNPaper/Dates';
import { useFormContext } from 'react-hook-form';
import HelpButton from '@/components/HelpButton';
import { useTranslation } from 'react-i18next';

const FDatePickerInput = React.forwardRef<
  React.ElementType<DatePickerInputFormProps>,
  DatePickerInputFormProps
>((props: DatePickerInputFormProps, ref): React.ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    label,
    labelStyle,
    error,
    onChangeValue,
    style,
    defaultValue,
    containerStyle,
    secureTextEntry,
    keyboardType,
    validators,
    placeholder,
    showAsterix = true,
    clearStateFunc,
    withMargin = true,
    withError = true,
    withHelpButton = false,
    helpFieldName,
    name,
    ...inputProps
  } = props;

  let realDefaultValue: Date | undefined =
    inputProps.value !== undefined ? inputProps.value : defaultValue;

  const [inputValue, setInputValue] = useState<Date | undefined>(realDefaultValue);

  const { setError } = useFormContext();

  if (clearStateFunc) {
    clearStateFunc(() => {
      setInputValue(undefined);
    });
  }

  useEffect(() => {
    setInputValue(realDefaultValue);
  }, [defaultValue, inputProps.value]);

  const required =
    showAsterix && validators && validators.indexOf(AvailableValidationRules.required) > -1 ? (
      <Text style={{ color: theme.colors.secondary }}> *</Text>
    ) : null;

  // Create the label with help button if needed
  const labelWithHelp =
    withHelpButton && (helpFieldName || name) ? (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text>
          {t(label)}
          {required}
        </Text>
        <HelpButton fieldName={helpFieldName || name || ''} size={16} style={{ marginLeft: 4 }} />
      </View>
    ) : required ? (
      <>
        {t(label)}
        {required}
      </>
    ) : (
      t(label)
    );

  return (
    <>
      <View
        style={[
          styles.container,
          withMargin
            ? {
                marginBottom: 10,
              }
            : null,
          containerStyle,
        ]}
      >
        <DatePickerInput
          ref={ref}
          label={labelWithHelp}
          placeholder={(placeholder || '') + (!label ? required : '')}
          style={[styles.input, style]}
          {...inputProps}
          hasError={error?.message !== undefined}
          hideValidationErrors={true}
          locale={'fr'}
          onChange={(text) => {
            setInputValue(text);
            if (onChangeValue) {
              onChangeValue(text);
            }
          }}
          onValidationError={(error) => {
            setInputValue('');
            if (error) {
              setError(inputProps.name, { message: error, type: 'manual' });
            }
          }}
          theme={{
            roundness: 24,
          }}
          value={inputValue}
          inputMode={'start'}
          mode="flat"
          dense={true}
          underlineColor={'transparent'}
          activeUnderlineColor={theme.colors.primary}
          underlineStyle={{ height: 0 }}
        />
        {withError && error?.message ? (
          <Text
            variant={'labelMedium'}
            style={{
              color: theme.colors.error,
              marginHorizontal: 18,
              bottom: -18,
              flex: 1,
              position: 'absolute',
            }}
          >
            {error?.message}
          </Text>
        ) : null}
      </View>
    </>
  );
});

FDatePickerInput.displayName = 'DatePickerInput';

export default FDatePickerInput;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    marginTop: 21,
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderStyle: 'solid',
    borderRadius: Border.br_3xs,
    backgroundColor: Color.blanc,
  },
  accessory: {
    width: Dimensions.get('window').width,
    height: 48,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    paddingHorizontal: 8,
  },
});
